"use client";

import { NativeLink as Link } from "@/components/native-link";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from "react";
import { useParams } from "react-router-dom";
import { SessionDetailToolbar } from "@/components/session-detail-toolbar";
import { api, type SessionDetail } from "@/lib/api";
import { useAuthProfile, useRoleImpersonation } from "@/lib/auth-context";
import { isHiddenByRoleScope } from "@/lib/session-role-scope";
import { layoutMarkerLanes } from "@/lib/session-perception-timeline";
import { timelineTranscriptSnippet, type TimelineUtterance } from "@/lib/timeline-transcript-snippet";
import {
  avatarGradientForUserKey,
  displayTopic,
  displayUserName,
  initialsFrom,
  sessionUserAvatarKey,
} from "@/lib/sessions";

type Metric = { label: string; value: string; trend?: string; warn?: boolean };
type Series = { label: string; color: string; values: number[] };
type HeatCell = { text: string; cls: string };
type Report = {
  schemaVersion?: string;
  status?: string;
  seriesSource?: string;
  hero?: { eyebrow?: string; title?: string; subtitle?: string; pills?: string[] };
  metrics?: Metric[];
  takeaway?: string;
  takeawayFull?: string;
  takeawayLayers?: string[];
  driversGood?: Array<{ title: string; detail?: string; width?: number }>;
  driversBad?: Array<{ title: string; detail?: string; width?: number }>;
  timeline?: {
    duration?: number;
    ticks?: Array<{ minute: number; label: string }>;
    series?: Series[];
    events?: Array<{ minute: number; label: string; color?: string }>;
  };
  journey?: Array<{ time: string; state: string; cause: string }>;
  moments?: Array<{ at: number; label: string; detail: string; tone?: string; icon?: string }>;
  heatmap?: Array<{ topic: string; cells: HeatCell[] }>;
  radar?: Array<{ label: string; value: number }>;
  pressure?: {
    values?: number[];
    events?: Array<{ index: number; label: string; color?: string; minute?: number }>;
    summary?: string;
  };
  fingerprint?: Array<{ label: string; values: number[] }>;
  behaviorMap?: Array<{ left: string; right: string }>;
  balance?: { userPct: number; agentPct: number; userTurns: number; agentTurns: number };
  video?: {
    summary?: string;
    signals?: string;
    appearance?: string;
    environment?: string;
    movement?: string;
    notes?: {
      signals?: string[];
      appearance?: string[];
      environment?: string[];
      movement?: string[];
    };
    chips?: string[];
    quality?: Record<string, number> | null;
  };
  evidence?: Array<{
    id: number;
    title: string;
    confidence?: string;
    language?: string;
    voice?: string;
    video?: string;
    context?: string;
    combined?: string;
  }>;
  utterances?: Array<{
    startMs: number;
    speaker: string;
    text: string;
    kind: string;
    signals?: string[];
  }>;
};

const NAV = [
  ["overview", "Overview"],
  ["timeline", "Dynamics"],
  ["journey", "State journey"],
  ["moments", "Key moments"],
  ["fingerprint", "Profile"],
  ["balance", "Balance"],
  ["transcript", "Transcript"],
  ["video", "Video notes"],
] as const;

function fmtClock(sec: number) {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function TakeawayBlock({
  short,
  full,
  layers,
}: {
  short: string;
  full?: string;
  layers?: string[];
}) {
  const [expanded, setExpanded] = useState(false);
  const fullText = (full && full.trim().length > 0 ? full : short) || "";
  const body = expanded ? fullText : short;
  const canExpand =
    fullText.length > (short?.length || 0) + 15
    || (short?.length || 0) > 200;
  return (
    <div className={`iprof-takeaway${expanded ? " is-expanded" : ""}`}>
      <p className="iprof-summary">{body}</p>
      {layers?.length ? (
        <div className="iprof-tags" style={{ marginTop: 10 }}>
          {layers.map((layer) => (
            <span key={layer} className="iprof-tag"><i />{layer}</span>
          ))}
        </div>
      ) : null}
      {canExpand ? (
        <button
          type="button"
          className="iprof-takeaway-toggle"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      ) : null}
    </div>
  );
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n));
}

/**
 * Clamp hover tip horizontally inside the chart outer box.
 * Vertical overflow is allowed (parent overflow:visible) so transcript is not clipped.
 */
function useClampedChartTip(
  active: boolean,
  anchorX: number | null,
  outerRef: RefObject<HTMLElement | null>,
  tipRef: RefObject<HTMLElement | null>,
  contentKey = "",
): CSSProperties {
  const [left, setLeft] = useState(8);
  useLayoutEffect(() => {
    if (!active || anchorX == null || !outerRef.current || !tipRef.current) return;
    const tipW = Math.max(tipRef.current.offsetWidth, 200);
    const maxLeft = Math.max(8, outerRef.current.clientWidth - tipW - 8);
    setLeft(clamp(anchorX - tipW / 2, 8, maxLeft));
  }, [active, anchorX, contentKey, outerRef, tipRef]);
  return { left, top: 8 };
}

function smoothPath(pts: Array<readonly [number, number]>, yLo: number, yHi: number): string {
  if (!pts.length) return "";
  const fmt = (x: number, y: number) => `${x.toFixed(1)},${y.toFixed(1)}`;
  if (pts.length === 1) return `M${fmt(pts[0][0], pts[0][1])}`;
  if (pts.length === 2) return `M${fmt(pts[0][0], pts[0][1])} L${fmt(pts[1][0], pts[1][1])}`;
  const cy = (y: number) => clamp(y, yLo, yHi);
  let d = `M${fmt(pts[0][0], pts[0][1])}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[Math.max(0, i - 1)];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[Math.min(pts.length - 1, i + 2)];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = cy(p1[1] + (p2[1] - p0[1]) / 6);
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = cy(p2[1] - (p3[1] - p1[1]) / 6);
    d += ` C${fmt(c1x, c1y)} ${fmt(c2x, c2y)} ${fmt(p2[0], p2[1])}`;
  }
  return d;
}

function sampleAt(values: number[], t01: number) {
  if (!values.length) return 0;
  const x = clamp(t01, 0, 1) * (values.length - 1);
  const i = Math.floor(x);
  const f = x - i;
  const a = values[i];
  const b = values[Math.min(values.length - 1, i + 1)];
  return Math.round(a + (b - a) * f);
}

function pressureSampleIndex(
  ev: { index: number; minute?: number },
  valuesLen: number,
  durationMin: number,
) {
  const last = Math.max(valuesLen - 1, 1);
  if (ev.minute != null && Number.isFinite(ev.minute) && durationMin > 0) {
    return clamp((ev.minute / durationMin) * last, 0, last);
  }
  if (valuesLen > 12 && ev.index >= 0 && ev.index <= 11) {
    return (ev.index / 11) * last;
  }
  return clamp(ev.index, 0, last);
}

function SectionTitle({ title, copy }: { title: string; copy: string }) {
  return (
    <div className="iprof-section-title">
      <h2>{title}</h2>
      <p>{copy}</p>
    </div>
  );
}

function Panel({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <article className="iprof-card">
      {title ? <h3>{title}</h3> : null}
      {children}
    </article>
  );
}

function TimelineChart({
  data,
  utterances,
  durationSec,
}: {
  data: NonNullable<Report["timeline"]>;
  utterances?: TimelineUtterance[];
  durationSec?: number;
}) {
  const series = data.series || [];
  const ticks = data.ticks || [];
  const duration = Math.max(data.duration || 1, 0.1);
  const w = 1060;
  const h = 330;
  const padL = 48;
  const padR = 24;
  const bottom = 36;
  const plotW = w - padL - padR;
  const [hoverMin, setHoverMin] = useState<number | null>(null);
  const [anchorX, setAnchorX] = useState<number | null>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const tipContentKey = `${hoverMin ?? ""}:${utterances?.length ?? 0}:${(data.events || []).length}`;
  const tipStyle = useClampedChartTip(
    hoverMin != null,
    anchorX,
    outerRef,
    tipRef,
    tipContentKey,
  );

  const laid = layoutMarkerLanes(
    (data.events || []).map((ev, i) => ({
      ...ev,
      id: `${ev.label}-${i}`,
      x: padL + (clamp(ev.minute, 0, duration) / duration) * plotW,
    })),
    padL,
    w - padR,
  );
  const lanes = Math.max(1, ...laid.map((e) => e.lane + 1), 1);
  const top = 14 + lanes * 16;
  const plotH = h - top - bottom;

  const valuesAt = (minute: number) => series.map((s) => ({
    label: s.label,
    color: s.color,
    value: sampleAt(s.values || [], minute / duration),
  }));

  const yOf = (v: number) => top + (1 - clamp(v, 0, 100) / 100) * plotH;

  const onMove = (ev: MouseEvent<SVGSVGElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const outer = outerRef.current?.getBoundingClientRect();
    const x = ((ev.clientX - rect.left) / rect.width) * w;
    const m = clamp(((x - padL) / plotW) * duration, 0, duration);
    setHoverMin(m);
    if (outer) setAnchorX(ev.clientX - outer.left);
  };

  const clearHover = () => {
    setHoverMin(null);
    setAnchorX(null);
  };

  return (
    <div className="iprof-timeline" ref={outerRef}>
      <div className="iprof-timeline-scroll">
        <svg
          className="iprof-timeline-svg"
          viewBox={`0 0 ${w} ${h}`}
          role="img"
          aria-label="Interaction timeline"
          onMouseMove={onMove}
          onMouseLeave={clearHover}
        >
          {[0, 25, 50, 75, 100].map((v) => {
            const y = yOf(v);
            return (
              <g key={v}>
                <line x1={padL} y1={y} x2={w - padR} y2={y} className="iprof-grid" />
                <text x={padL - 8} y={y + 4} className="iprof-svg-axis" textAnchor="end">{v}</text>
              </g>
            );
          })}
          {series.map((s) => {
            const pts = (s.values || []).map((v, i) => {
              const x = padL + (i / Math.max((s.values || []).length - 1, 1)) * plotW;
              return [x, yOf(v)] as const;
            });
            return (
              <g key={s.label}>
                <path
                  d={smoothPath(pts, top, top + plotH)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
            );
          })}
          {laid.map((ev) => {
            const cy = 10 + ev.lane * 16;
            return (
              <g key={ev.id}>
                <line x1={ev.x} y1={cy} x2={ev.x} y2={h - 35} stroke={ev.color || "#0968f2"} strokeWidth="1.2" strokeDasharray="4 4" opacity="0.7" />
                <circle cx={ev.x} cy={cy} r={5} fill={ev.color || "#0968f2"} />
                <text
                  x={ev.textX}
                  y={cy + 4}
                  className="iprof-svg-label"
                  textAnchor={ev.textAnchor}
                >
                  <title>{ev.label}</title>
                  {ev.displayLabel}
                </text>
              </g>
            );
          })}
          {ticks.map((t) => {
            const x = padL + (t.minute / duration) * plotW;
            return <text key={t.label} x={x} y={h - 10} className="iprof-svg-axis" textAnchor="middle">{t.label}</text>;
          })}
          {hoverMin != null ? (() => {
            const x = padL + (hoverMin / duration) * plotW;
            return (
              <g>
                <line x1={x} y1={top - 4} x2={x} y2={h - 28} stroke="#334155" strokeWidth="1.2" opacity="0.55" />
              </g>
            );
          })() : null}
        </svg>
      </div>
      {hoverMin != null ? (
        <div
          ref={tipRef}
          className="iprof-timeline-tip"
          style={tipStyle}
        >
          <strong>{fmtClock(hoverMin * 60)}</strong>
          {valuesAt(hoverMin).map((v) => (
            <div key={v.label}><i style={{ background: v.color }} />{v.label} <b>{v.value}</b></div>
          ))}
          {utterances?.length ? (() => {
            const snip = timelineTranscriptSnippet(
              utterances,
              hoverMin * 60,
              durationSec ?? (data.duration ?? 0) * 60,
            );
            if (!snip.agent && !snip.user) return null;
            return (
              <div className="iprof-timeline-tip-transcript">
                {snip.agent ? (
                  <p className="iprof-timeline-tip-agent">
                    <span>Agent</span>
                    {snip.agent}
                  </p>
                ) : null}
                {snip.user ? (
                  <p className="iprof-timeline-tip-user">
                    <span>User</span>
                    {snip.user}
                  </p>
                ) : null}
              </div>
            );
          })() : null}
        </div>
      ) : null}
    </div>
  );
}

function RadarChart({ items }: { items: Array<{ label: string; value: number }> }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !items.length) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const size = 280;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const cx = size / 2;
    const cy = size / 2;
    const r = 92;
    const n = items.length;
    ctx.clearRect(0, 0, size, size);
    ctx.strokeStyle = "#dce5ef";
    for (let ring = 1; ring <= 4; ring += 1) {
      ctx.beginPath();
      for (let i = 0; i <= n; i += 1) {
        const a = (Math.PI * 2 * (i % n)) / n - Math.PI / 2;
        const rr = (r * ring) / 4;
        const px = cx + Math.cos(a) * rr;
        const py = cy + Math.sin(a) * rr;
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.stroke();
    }
    ctx.beginPath();
    items.forEach((it, i) => {
      const a = (Math.PI * 2 * i) / n - Math.PI / 2;
      const rr = r * (Math.max(0, Math.min(100, it.value)) / 100);
      const px = cx + Math.cos(a) * rr;
      const py = cy + Math.sin(a) * rr;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    });
    ctx.closePath();
    ctx.fillStyle = "rgba(9,104,242,0.18)";
    ctx.strokeStyle = "#0968f2";
    ctx.lineWidth = 2;
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = "#40546d";
    ctx.font = "11px Inter, sans-serif";
    ctx.textAlign = "center";
    items.forEach((it, i) => {
      const a = (Math.PI * 2 * i) / n - Math.PI / 2;
      ctx.fillText(it.label, cx + Math.cos(a) * (r + 22), cy + Math.sin(a) * (r + 22));
    });
  }, [items]);
  return <canvas ref={ref} className="iprof-radar" />;
}

function TenseMomentPath({
  values,
  events,
  summary,
  durationMin,
  metricLabel,
  metricColor,
}: {
  values: number[];
  events: Array<{ index: number; label: string; color?: string; minute?: number }>;
  summary?: string;
  durationMin?: number;
  metricLabel?: string;
  metricColor?: string;
}) {
  const [hoverT, setHoverT] = useState<number | null>(null);
  const [anchorX, setAnchorX] = useState<number | null>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);
  const tipStyle = useClampedChartTip(
    hoverT != null,
    anchorX,
    outerRef,
    tipRef,
    `${hoverT ?? ""}:${events.length}`,
  );
  if (!values.length) {
    return <p className="muted">Tense-moment path fills once scores exist for this call.</p>;
  }
  const w = 760;
  const h = 245;
  const padL = 38;
  const padR = 16;
  const top = 18;
  const bottom = 32;
  const plotW = w - padL - padR;
  const plotH = h - top - bottom;
  const dur = Math.max(durationMin || 1, 0.1);
  const label = metricLabel || "Comfort";
  const color = metricColor || "#0968f2";
  const yOf = (v: number) => top + (1 - clamp(v, 0, 100) / 100) * plotH;
  const xOfT = (t: number) => padL + t * plotW;
  const pts = values.map((v, i) => {
    const t = i / Math.max(values.length - 1, 1);
    return [xOfT(t), yOf(v)] as const;
  });
  const range = Math.max(...values) - Math.min(...values);
  const dipI = values.reduce((best, v, i) => (v < values[best] ? i : best), 0);
  const dipT = dipI / Math.max(values.length - 1, 1);
  const nearEvents = (t: number) => events.filter((ev) => {
    const et = pressureSampleIndex(ev, values.length, dur) / Math.max(values.length - 1, 1);
    return Math.abs(et - t) < 0.08;
  });

  const onMove = (ev: MouseEvent<SVGSVGElement>) => {
    const rect = ev.currentTarget.getBoundingClientRect();
    const outer = outerRef.current?.getBoundingClientRect();
    const x = ((ev.clientX - rect.left) / rect.width) * w;
    setHoverT(clamp((x - padL) / plotW, 0, 1));
    if (outer) setAnchorX(ev.clientX - outer.left);
  };

  const clearHover = () => {
    setHoverT(null);
    setAnchorX(null);
  };

  return (
    <div className="iprof-pressure-wrap" ref={outerRef}>
      <svg
        className="iprof-pressure-svg"
        viewBox={`0 0 ${w} ${h}`}
        aria-label="Response to a tense moment"
        onMouseMove={onMove}
        onMouseLeave={clearHover}
      >
        {[20, 40, 60, 80, 100].map((v) => {
          const y = yOf(v);
          return (
            <g key={v}>
              <line x1={padL} y1={y} x2={w - padR} y2={y} className="iprof-grid" />
              <text x={4} y={y + 4} className="iprof-svg-axis">{v}</text>
            </g>
          );
        })}
        <path
          d={smoothPath(pts, top, top + plotH)}
          fill="none"
          stroke={color}
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {range >= 4 ? (
          <circle cx={xOfT(dipT)} cy={yOf(values[dipI])} r={7} fill="none" stroke="#e11d48" strokeWidth="2" />
        ) : null}
        {events.map((ev, i) => {
          const idx = pressureSampleIndex(ev, values.length, dur);
          const t = idx / Math.max(values.length - 1, 1);
          const y = yOf(sampleAt(values, t));
          return <circle key={`${ev.label}-${i}`} cx={xOfT(t)} cy={y} r={5} fill={ev.color || "#e11d48"} />;
        })}
        <text x={padL} y={h - 8} className="iprof-svg-axis">{fmtClock(0)}</text>
        <text x={w - padR} y={h - 8} className="iprof-svg-axis" textAnchor="end">{fmtClock(dur * 60)}</text>
        {hoverT != null ? (
          <line x1={xOfT(hoverT)} y1={top} x2={xOfT(hoverT)} y2={h - bottom + 4} stroke="#334155" strokeWidth="1.2" opacity="0.55" />
        ) : null}
      </svg>
      {hoverT != null ? (
        <div
          ref={tipRef}
          className="iprof-timeline-tip"
          style={tipStyle}
        >
          <strong>{fmtClock(hoverT * dur * 60)}</strong>
          <div><i style={{ background: color }} />{label} <b>{sampleAt(values, hoverT)}</b></div>
          {nearEvents(hoverT).map((ev) => (
            <div key={ev.label}><i style={{ background: ev.color || "#e11d48" }} />{ev.label}</div>
          ))}
        </div>
      ) : null}
      {events.length ? (
        <div className="iprof-legend">
          {events.map((ev, i) => (
            <span key={`${ev.label}-${i}`}><i style={{ background: ev.color || "#e11d48" }} />{ev.label}</span>
          ))}
        </div>
      ) : null}
      <p className="iprof-chart-hint">
        {label} along the call (0–100). Hover for the score at that time. Dots are topic beats.
        {range < 4
          ? " A nearly flat line means this score barely moved."
          : " A dip is tension; a later rise is recovery. The ring marks the lowest point."}
      </p>
      {summary ? <p className="iprof-pressure-caption">{summary}</p> : null}
    </div>
  );
}

function Spark({ values, color }: { values: number[]; color: string }) {
  if (!values.length) return null;
  const w = 140;
  const h = 36;
  const d = values
    .map((v, i) => {
      const x = (i / Math.max(values.length - 1, 1)) * w;
      const y = h - (v / 100) * h;
      return `${i ? "L" : "M"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={d} fill="none" stroke={color} strokeWidth="1.8" />
    </svg>
  );
}

function VideoField({
  title,
  prose,
  bullets,
}: {
  title: string;
  prose?: string;
  bullets?: string[];
}) {
  const items = (bullets || []).filter(Boolean);
  return (
    <Panel title={title}>
      {prose ? <p>{prose}</p> : null}
      {items.length ? (
        <ul className="iprof-notes">
          {items.map((b) => <li key={b}>{b}</li>)}
        </ul>
      ) : null}
      {!prose && !items.length ? <p className="muted">—</p> : null}
    </Panel>
  );
}

const AGENT_AVATAR_GRADIENT = "linear-gradient(135deg,#286fd7,#724ed4)";

function Face({
  src,
  initials,
  gradient,
  user,
}: {
  src?: string | null;
  initials: string;
  gradient?: string;
  user: boolean;
}) {
  if (src) {
    return <img className="iprof-face" src={src} alt="" />;
  }
  return (
    <span
      className={`iprof-face iprof-face-fallback${user ? " is-user" : " is-agent"}`}
      style={{ background: gradient }}
    >
      {initials}
    </span>
  );
}

export function SessionPerceptionPage() {
  const { id = "" } = useParams();
  const profile = useAuthProfile();
  const { impersonation } = useRoleImpersonation();
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [loadError, setLoadError] = useState("");
  const [txFilter, setTxFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const session = await api.getSession(id);
        if (cancelled) return;
        if (
          impersonation
          && isHiddenByRoleScope(session, profile, { ignoreOwnerException: true })
        ) {
          setDetail(null);
          setReport(null);
          setLoadError("Session not found");
          return;
        }
        setDetail(session);
        const url = session.perceptionReportUrl;
        if (!url) {
          setReport(null);
          return;
        }
        const res = await fetch(url);
        if (!res.ok) {
          setReport(null);
          return;
        }
        const json = (await res.json()) as Report;
        if (!cancelled) setReport(json);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : "Failed to load");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, impersonation?.roleId, profile?.roleId, profile?.allowedAgentIds?.join("|")]);

  const utterances = useMemo(() => {
    const rows = report?.utterances || [];
    if (txFilter === "all") return rows;
    return rows.filter((u) => u.kind === txFilter);
  }, [report, txFilter]);

  const status = String(detail?.perceptionReportStatus || report?.status || "").toLowerCase();
  const pending = !report && (status === "pending" || !status);
  const timelineCopy = report?.seriesSource === "live_perception"
    ? "Live ticks with turning points marked."
    : "Estimated from this call’s transcript and voice labels. Traces should move with the conversation — not sit as five flat rails.";

  const userLabel = detail ? displayUserName(detail) : "User";
  const userInitials = initialsFrom(userLabel);
  const userAvatarGradient = detail
    ? avatarGradientForUserKey(sessionUserAvatarKey({ ...detail, sessionId: id }))
    : avatarGradientForUserKey(id);
  const agentInitials = initialsFrom(detail?.agentName || "Agent");

  const tone = (kind: string) => (
    kind === "positive" ? "#0aa878" : kind === "uncertain" ? "#e14d5a" : kind === "critical" ? "#f59e0b" : "#96a8bc"
  );

  return (
    <div className="session-detail-page">
    <SessionDetailToolbar backHref={`/sessions/${id}`} backLabel="Back to session" />
    <div className="iprof">
      <header className="iprof-hero">
        <p className="iprof-eyebrow">{report?.hero?.eyebrow || "Pulse · Interaction Profile"}</p>
        <h1>{report?.hero?.title || "Interaction Profile"}</h1>
        <p className="iprof-sub">
          {report?.hero?.subtitle
            || (detail ? [displayUserName(detail), displayTopic(detail)].filter(Boolean).join(" · ") : "")
            || "Retrospective analysis of this conversation"}
        </p>
        <div className="iprof-pills">
          {(report?.hero?.pills || []).map((p) => (
            <span key={p} className="iprof-pill"><i />{p}</span>
          ))}
        </div>
      </header>

      {pending ? (
        <div className="iprof-banner">Report is still building. Waveform, transcript, and video notes can appear first — refresh this page after postprocess finishes.</div>
      ) : null}
      {status === "partial" ? (
        <div className="iprof-banner">Partial report: some channels were missing; available notes and scores are shown.</div>
      ) : null}
      {status === "failed" || loadError ? (
        <div className="iprof-banner warn">{loadError || "Report job failed. Session detail still has the four operator tabs."}</div>
      ) : null}

      <nav className="iprof-nav">
        {NAV.map(([href, label]) => (
          <a key={href} href={`#${href}`}>{label}</a>
        ))}
      </nav>

      <section id="overview" className="iprof-section">
        <SectionTitle title="Interaction overview" copy="How engagement, comfort, concern, and clarity moved in this conversation." />
        <div className="iprof-metrics">
          {(report?.metrics || [
            { label: "Engagement", value: "—" },
            { label: "Comfort", value: "—" },
            { label: "Concern", value: "—" },
            { label: "Clarity", value: "—" },
          ]).map((m) => (
            <article key={m.label} className={`iprof-card iprof-metric ${m.warn ? "warn" : ""}`}>
              <span>{m.label}</span>
              <b>{m.value}</b>
              <em className={m.warn ? "warn" : ""}>{m.trend || " "}</em>
            </article>
          ))}
        </div>
        <div className="iprof-grid-2">
          <Panel title="Overall takeaway">
            <TakeawayBlock
              short={report?.takeaway || "Waiting for Interaction Profile JSON."}
              full={report?.takeawayFull || report?.takeaway}
              layers={report?.takeawayLayers}
            />
          </Panel>
          <Panel title="What most shaped the interaction">
            {(report?.driversGood || []).length ? (
              <div className="iprof-driver-group">
                <span className="iprof-badge good">Helped</span>
                {(report?.driversGood || []).map((d) => (
                  <div key={d.title} className="iprof-driver">
                    <div><strong>{d.title}</strong><small>{d.detail}</small></div>
                    <div className="iprof-track"><i style={{ width: `${d.width || 50}%` }} /></div>
                  </div>
                ))}
              </div>
            ) : null}
            {(report?.driversBad || []).length ? (
              <div className="iprof-driver-group negative">
                <span className="iprof-badge warn">Strained</span>
                {(report?.driversBad || []).map((d) => (
                  <div key={d.title} className="iprof-driver">
                    <div><strong>{d.title}</strong><small>{d.detail}</small></div>
                    <div className="iprof-track"><i style={{ width: `${d.width || 50}%` }} /></div>
                  </div>
                ))}
              </div>
            ) : null}
            {!report?.driversGood?.length && !report?.driversBad?.length ? (
              <p className="muted">Drivers appear once the report job finishes.</p>
            ) : null}
          </Panel>
        </div>
      </section>

      <section id="timeline" className="iprof-section">
        <SectionTitle title="Interaction timeline" copy={timelineCopy} />
        <Panel>
          {report?.timeline?.series?.length ? (
            <>
              <TimelineChart
                data={report.timeline}
                utterances={report.utterances}
                durationSec={(report.timeline.duration ?? 0) * 60}
              />
              <div className="iprof-legend">
                {report.timeline.series.map((s) => (
                  <span key={s.label}><i style={{ background: s.color }} />{s.label}</span>
                ))}
              </div>
            </>
          ) : (
            <p className="muted">Timeline fills once scores exist for this call.</p>
          )}
        </Panel>
      </section>

      <section id="journey" className="iprof-section">
        <SectionTitle title="Behavioral journey" copy="Topic windows in order — not isolated emotion labels." />
        <Panel>
          {(report?.journey || []).length ? (
            <div className="iprof-journey">
              {(report?.journey || []).map((j, i) => (
                <div key={`${j.time}-${j.state}`} className="iprof-journey-row">
                  {i > 0 ? <span className="iprof-arrow" aria-hidden>→</span> : null}
                  <div className="iprof-journey-node">
                    <div className="time">{j.time}</div>
                    <div className="state">{j.state}</div>
                    <div className="cause">{j.cause}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">Journey windows fill once the transcript has timed turns.</p>
          )}
        </Panel>
      </section>

      <section id="moments" className="iprof-section">
        <SectionTitle title="Key moments" copy="Beats after which behavior visibly shifted." />
        <div className="iprof-grid-2">
          <Panel>
            {(report?.moments || []).length ? (
              <div className="iprof-turning">
                {(report?.moments || []).map((m, i) => (
                  <div key={`${m.at}-${i}`} className="iprof-turning-row">
                    <div className="iprof-turning-icon">{m.icon || (m.tone === "warn" ? "!" : "↗")}</div>
                    <div>
                      <Link href={`/sessions/${id}?t=${encodeURIComponent(String(m.at))}`}>
                        {fmtClock(m.at)} · {m.label}
                      </Link>
                      <small>{m.detail}</small>
                    </div>
                    <span className={`iprof-badge ${m.tone === "warn" ? "warn" : "good"}`}>
                      {m.tone === "warn" ? "Attention" : "Useful"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="muted">Key moments appear after the Interaction Profile job runs.</p>
            )}
          </Panel>
          <Panel title="Topics × reaction">
            {(report?.heatmap || []).length ? (
              <div className="iprof-heatmap-wrap">
                <table className="iprof-heatmap">
                  <thead>
                    <tr>
                      <th>Topic</th>
                      <th>Engagement</th>
                      <th>Comfort</th>
                      <th>Clarity</th>
                      <th>Concern</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(report?.heatmap || []).map((row) => (
                      <tr key={row.topic}>
                        <td>{row.topic}</td>
                        {(row.cells || []).slice(0, 4).map((c, i) => (
                          <td key={`${row.topic}-${i}`}><span className={`iprof-hm ${c.cls}`}>{c.text}</span></td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="muted">Topic heatmap fills with journey topics and key moments.</p>
            )}
          </Panel>
        </div>
      </section>

      <section id="fingerprint" className="iprof-section">
        <SectionTitle title="Behavioral profile" copy="Tendencies within this interaction only." />
        <div className="iprof-grid-2">
          <Panel title="Behavioral profile radar">
            <div className="iprof-chart-box">
              {(report?.radar || []).length
                ? <RadarChart items={report?.radar || []} />
                : <p className="muted">Radar fills once scores exist.</p>}
            </div>
          </Panel>
          <Panel title="What tends to shift scores">
            <div className="iprof-behavior-map">
              {(report?.behaviorMap || []).length
                ? report?.behaviorMap?.map((row) => (
                  <div key={`${row.left}-${row.right}`} className="iprof-map-row">
                    <span className="left">{row.left}</span>
                    <span className="mid">→</span>
                    <span className="right">{row.right}</span>
                  </div>
                ))
                : <p className="muted">Trigger → effect pairs fill after the Interaction Profile job runs.</p>}
            </div>
          </Panel>
          <Panel title="Response to a tense moment">
            <TenseMomentPath
              values={report?.pressure?.values || []}
              events={report?.pressure?.events || []}
              summary={report?.pressure?.summary}
              durationMin={report?.timeline?.duration}
              metricLabel="Comfort"
              metricColor={(report?.timeline?.series || []).find((s) => s.label === "Comfort")?.color || "#22c55e"}
            />
          </Panel>
          <Panel title="Interaction fingerprint">
            <div className="iprof-fingerprint">
              {(report?.fingerprint || []).map((s) => (
                <div key={s.label} className="iprof-fingerprint-row">
                  <label>{s.label}</label>
                  <div className="iprof-spark"><Spark values={s.values} color="#0968f2" /></div>
                </div>
              ))}
              {!report?.fingerprint?.length ? <p className="muted">Sparklines fill once scores exist.</p> : null}
            </div>
          </Panel>
        </div>
      </section>

      <section id="balance" className="iprof-section">
        <SectionTitle title="Interaction balance" copy="Conversation structure and each party’s participation." />
        <Panel>
          <div className="iprof-balance">
            <div>
              <span className="iprof-badge">Speaking time</span>
              <div className="iprof-bar-row">
                <div className="top"><span>User</span><b>{report?.balance?.userPct ?? "—"}%</b></div>
                <div className="iprof-track"><i style={{ width: `${report?.balance?.userPct ?? 0}%` }} /></div>
              </div>
              <div className="iprof-bar-row">
                <div className="top"><span>Agent</span><b>{report?.balance?.agentPct ?? "—"}%</b></div>
                <div className="iprof-track"><i style={{ width: `${report?.balance?.agentPct ?? 0}%` }} /></div>
              </div>
            </div>
            <div>
              <span className="iprof-badge">Turns</span>
              <div className="iprof-bar-row">
                <div className="top"><span>User turns</span><b>{report?.balance?.userTurns ?? 0}</b></div>
                <div className="iprof-track"><i style={{ width: `${Math.min(100, (report?.balance?.userTurns ?? 0) * 8)}%` }} /></div>
              </div>
              <div className="iprof-bar-row">
                <div className="top"><span>Agent turns</span><b>{report?.balance?.agentTurns ?? 0}</b></div>
                <div className="iprof-track"><i style={{ width: `${Math.min(100, (report?.balance?.agentTurns ?? 0) * 8)}%` }} /></div>
              </div>
            </div>
          </div>
        </Panel>
      </section>

      <section id="transcript" className="iprof-section">
        <SectionTitle title="Transcript explorer" copy="Transcript fragments with behavioral-change markers." />
        <div className="iprof-filters">
          {["all", "uncertain", "positive", "critical"].map((k) => (
            <button key={k} type="button" className={txFilter === k ? "active" : ""} onClick={() => setTxFilter(k)}>
              {k}
            </button>
          ))}
        </div>
        <div className="iprof-transcript">
          {utterances.map((u, i) => (
            <div key={`${u.startMs}-${i}`} className="iprof-utterance" style={{ ["--tone" as string]: tone(u.kind) }}>
              <Link className="time" href={`/sessions/${id}?t=${encodeURIComponent(String(u.startMs / 1000))}`}>
                {fmtClock(u.startMs / 1000)}
              </Link>
              <Face
                src={u.speaker === "User" ? null : detail?.agentAvatarUrl}
                initials={u.speaker === "User" ? userInitials : agentInitials}
                gradient={u.speaker === "User" ? userAvatarGradient : AGENT_AVATAR_GRADIENT}
                user={u.speaker === "User"}
              />
              <div>
                <div className="iprof-utterance-role">{u.speaker}</div>
                <div className="text">{u.text}</div>
              </div>
              <div className="signals">
                {(u.signals || []).map((s) => <span key={s} className="iprof-signal">{s}</span>)}
              </div>
            </div>
          ))}
          {!utterances.length ? <Panel><p className="muted">No transcript lines yet.</p></Panel> : null}
        </div>
      </section>

      <section id="video" className="iprof-section">
        <SectionTitle title="Video notes" copy="Appearance, environment, movement, chips, and bullets from offline camera analysis when user video exists." />
        {(report?.video?.chips || []).length ? (
          <div className="iprof-tags" style={{ marginBottom: 12 }}>
            {(report?.video?.chips || []).map((c) => (
              <span key={c} className="iprof-tag"><i />{c}</span>
            ))}
          </div>
        ) : null}
        <div className="iprof-grid-2">
          {(["signals", "appearance", "environment", "movement"] as const).map((k) => (
            <VideoField
              key={k}
              title={k[0].toUpperCase() + k.slice(1)}
              prose={report?.video?.[k]}
              bullets={report?.video?.notes?.[k]}
            />
          ))}
        </div>
        {(report?.evidence || []).length ? (
          <>
            <SectionTitle title="Multimodal evidence" copy="Language, voice, video, and context for each key moment." />
            <div className="iprof-grid-2">
              {report?.evidence?.map((e) => (
                <Panel key={e.id}>
                  <div className="iprof-evidence-head">
                    <h3>{e.title}</h3>
                    <span className="iprof-badge">{e.confidence || "From session"}</span>
                  </div>
                  <div className="iprof-evidence-grid">
                    <div className="cell"><div className="k">Language</div><div className="v">{e.language || "—"}</div></div>
                    <div className="cell"><div className="k">Voice</div><div className="v">{e.voice || "—"}</div></div>
                    <div className="cell"><div className="k">Video</div><div className="v">{e.video || "—"}</div></div>
                    <div className="cell"><div className="k">Context</div><div className="v">{e.context || "—"}</div></div>
                    <div className="sum"><b>Combined</b><div>{e.combined || "—"}</div></div>
                  </div>
                </Panel>
              ))}
            </div>
          </>
        ) : null}
      </section>
    </div>
    </div>
  );
}
